import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Container,
    Button,
    CircularProgress,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    FileDownload as FileDownloadIcon,
    Print as PrintIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { format, parse, startOfMonth, endOfMonth } from 'date-fns';
import Layout from '../../components/layout/Layout';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import gstReportService from '../../services/gstReportService';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx';

const headRowProps = { sx: { bgcolor: 'grey.100' } };
const headCellSx = {
    color: 'text.primary',
    fontWeight: 700,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
    py: 1,
};
const bodyCellSx = {
    fontSize: '0.875rem',
};
const stripeRow = { '&:nth-of-type(odd)': { bgcolor: 'action.hover' } };

const num = (v) => formatCurrency(v ?? 0);

const emptyTotals = () => ({
    taxableValue: 0,
    integratedTax: 0,
    centralTax: 0,
    stateUtTax: 0,
    cess: 0,
});

/** First and last calendar days for [fromMonth, toMonth] inclusive (yyyy-MM). */
function monthRangeToDates(fromMonth, toMonth) {
    if (!fromMonth || !toMonth) return { startDate: '', endDate: '' };
    const from = parse(`${fromMonth}-01`, 'yyyy-MM-dd', new Date());
    const to = parse(`${toMonth}-01`, 'yyyy-MM-dd', new Date());
    const start = startOfMonth(from);
    const end = endOfMonth(to);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
}

/** `yyyy-MM` → `MM/yyyy` for Excel (e.g. 2026-04 → 04/2026). */
function formatMonthForExcel(ym) {
    if (!ym || typeof ym !== 'string') return '';
    const parts = ym.split('-');
    if (parts.length !== 2) return ym;
    const [y, m] = parts;
    return m && y ? `${m}/${y}` : ym;
}

function excelNum(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

/** Section 3.1 table: same row logic as the on-screen GSTR-3B table. */
function buildGstr31ReportRows(row1, row2ZeroRated, row3NilExempt, row4Rcm, row5NonGst) {
    const row = (prefix, r) => [
        `${prefix}${r.label}`,
        excelNum(r.taxable),
        excelNum(r.igst),
        excelNum(r.cgst),
        excelNum(r.sgst),
        excelNum(r.cess),
    ];
    return [
        row('(a) ', row1),
        row('(b) ', row2ZeroRated),
        row('(c) ', row3NilExempt),
        row('(d) ', row4Rcm),
        row('(e) ', row5NonGst),
    ];
}

/**
 * GSTR-3B Section 3.2 — Inter-state supplies by place of supply (template; per-state breakdown not in API yet).
 * Layout: two header rows with merged group titles (unregistered / composition / UIN).
 */
function buildGstr32Worksheet(fromMonth, toMonth) {
    const aoa = [
        ['From', formatMonthForExcel(fromMonth)],
        ['To', formatMonthForExcel(toMonth)],
        [],
        [
            'Place of supply (State/UT)',
            'Supplies to unregistered persons',
            '',
            'Supplies to composition taxable persons',
            '',
            'Supplies to UIN holders',
            '',
        ],
        [
            '',
            'Total taxable value',
            'Integrated tax',
            'Total taxable value',
            'Integrated tax',
            'Total taxable value',
            'Integrated tax',
        ],
        [
            'No data (not tracked in system – treat as 0 for filing checks)',
            0,
            0,
            0,
            0,
            0,
            0,
        ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [
        { s: { r: 3, c: 0 }, e: { r: 4, c: 0 } },
        { s: { r: 3, c: 1 }, e: { r: 3, c: 2 } },
        { s: { r: 3, c: 3 }, e: { r: 3, c: 4 } },
        { s: { r: 3, c: 5 }, e: { r: 3, c: 6 } },
    ];
    ws['!cols'] = [
        { wch: 30 },
        { wch: 22 },
        { wch: 18 },
        { wch: 34 },
        { wch: 22 },
        { wch: 26 },
        { wch: 18 },
    ];
    return ws;
}

/**
 * GSTR-3B Table 4 — Details of eligible Input Tax Credit (same figures as on-screen section 3).
 * Purchase ITC from books is shown under (5) All other ITC; other rows stay 0 until backend splits exist.
 */
function buildGstr4Worksheet(fromMonth, toMonth, inwardIn) {
    const inward = inwardIn || emptyTotals();
    const ig = excelNum(inward.integratedTax);
    const cg = excelNum(inward.centralTax);
    const sg = excelNum(inward.stateUtTax);
    const cs = excelNum(inward.cess);
    const aoa = [
        ['From', formatMonthForExcel(fromMonth)],
        ['To', formatMonthForExcel(toMonth)],
        [],
        ['Details', 'Integrated tax', 'Central tax', 'State/UT tax', 'Cess'],
        [],
        ['(A) ITC available (whether in full or part)', '', '', '', ''],
        ['(1) Import of goods', 0, 0, 0, 0],
        ['(2) Import of services', 0, 0, 0, 0],
        ['(3) Inward supplies liable to reverse charge (other than 1 & 2 above)', 0, 0, 0, 0],
        ['(4) Inward supplies from ISD', 0, 0, 0, 0],
        ['(5) All other ITC', ig, cg, sg, cs],
        [],
        ['(D) Ineligible ITC', '', '', '', ''],
        ['(1) As per section 17(5)', 0, 0, 0, 0],
        ['(2) Others', 0, 0, 0, 0],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
        { wch: 62 },
        { wch: 16 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
    ];
    return ws;
}

/**
 * GSTR-3B Table 5 — Exempt, nil-rated and non-GST inward supplies (inter-state vs intra-state).
 */
function buildGstr5Worksheet(fromMonth, toMonth, inward5) {
    const e1i = excelNum(inward5?.inwardExemptNilInterState);
    const e1a = excelNum(inward5?.inwardExemptNilIntraState);
    const ngi = excelNum(inward5?.inwardNonGstInterState);
    const nga = excelNum(inward5?.inwardNonGstIntraState);
    const aoa = [
        ['From', formatMonthForExcel(fromMonth)],
        ['To', formatMonthForExcel(toMonth)],
        [],
        ['Nature of supplies', 'Inter-State supplies', 'Intra-State supplies'],
        [],
        ['From a supplier under composition scheme; exempt and nil rated supply', e1i, e1a],
        ['Non-GST supply', ngi, nga],
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 58 }, { wch: 22 }, { wch: 22 }];
    return ws;
}

/** Excel tab names (≤31 chars, no \\ / ? * [ ] :). */
const GSTR3B_EXCEL_SHEETS = {
    /** Section 3.1 — Outward and inward reverse charge (summary) */
    OUTWARD: '3.1 Outward supplies',
    /** Section 3.2 — Inter-state supplies to unregistered, composition, UIN */
    INTERSTATE_B2C: '3.2 Inter-State B2C UIN',
    /** Table 4 — Eligible ITC */
    ELIGIBLE_ITC: '4 Eligible ITC',
    /** Table 5 — Exempt / nil / non-GST inward */
    EXEMPT_INWARD: '5 Exempt inward',
};

const GSTR3BReport = () => {
    const now = new Date();
    const defaultMonth = format(now, 'yyyy-MM');

    const [fromMonth, setFromMonth] = useState(defaultMonth);
    const [toMonth, setToMonth] = useState(defaultMonth);

    const { startDate, endDate } = useMemo(
        () => monthRangeToDates(fromMonth, toMonth),
        [fromMonth, toMonth]
    );

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState({
        outwardSupplies: null,
        inwardItc: null,
        netGstPayableEstimate: null,
        outwardNilRatedTaxableValue: 0,
        outwardExemptTaxableValue: 0,
        outwardNonGstTaxLineTaxableValue: 0,
        outwardNonGstBillTaxableValue: 0,
        salesRows: [],
        salesTaxRates: [],
        salesSummary: null,
        purchaseRows: [],
        purchaseTaxRates: [],
        purchaseSummary: null,
        inwardExemptNilInterState: 0,
        inwardExemptNilIntraState: 0,
        inwardNonGstInterState: 0,
        inwardNonGstIntraState: 0,
        startDate: null,
        endDate: null,
    });
    const [isExporting, setIsExporting] = useState(false);

    const fetchReportData = useCallback(async () => {
        if (!startDate || !endDate) return;
        try {
            setLoading(true);
            const gstr3b = await gstReportService.getGSTR3BReport(startDate, endDate);
            setReportData(gstr3b);
        } catch (error) {
            console.error('Error fetching GSTR-3B report:', error);
            toast.error('Failed to load GSTR-3B report');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const outward = reportData.outwardSupplies || emptyTotals();
    const inward = reportData.inwardItc || emptyTotals();

    /** Row 1: normal GST taxable sales */
    const row1 = useMemo(
        () => ({
            label: 'Outward taxable supplies (other than zero rated, nil rated and exempted)',
            taxable: outward.taxableValue ?? 0,
            igst: outward.integratedTax ?? 0,
            cgst: outward.centralTax ?? 0,
            sgst: outward.stateUtTax ?? 0,
            cess: outward.cess ?? 0,
        }),
        [outward]
    );

    const row2ZeroRated = useMemo(
        () => ({
            label: 'Outward taxable supplies (zero rated)',
            taxable: 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
        }),
        []
    );

    /** Row 3: nil-rated + exempt line items (tax master) */
    const row3NilExempt = useMemo(() => {
        const nil = Number(reportData.outwardNilRatedTaxableValue) || 0;
        const ex = Number(reportData.outwardExemptTaxableValue) || 0;
        return {
            label: 'Other outward supplies (nil rated, exempted)',
            taxable: nil + ex,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
        };
    }, [reportData.outwardNilRatedTaxableValue, reportData.outwardExemptTaxableValue]);

    const row4Rcm = useMemo(
        () => ({
            label: 'Inward supplies (liable to reverse charge)',
            taxable: 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
        }),
        []
    );

    /** Row (e): label kept for GSTR-3B layout; amounts always zero (not shown on this return). */
    const row5NonGst = useMemo(
        () => ({
            label: 'Non-GST outward supplies',
            taxable: 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
        }),
        []
    );

    /** GSTR-3B Table 5 — inward exempt/nil and non-GST (supplier GSTIN state vs company GSTIN). */
    const inwardTable5 = useMemo(
        () => ({
            exemptNilInter: Number(reportData.inwardExemptNilInterState) || 0,
            exemptNilIntra: Number(reportData.inwardExemptNilIntraState) || 0,
            nonGstInter: Number(reportData.inwardNonGstInterState) || 0,
            nonGstIntra: Number(reportData.inwardNonGstIntraState) || 0,
        }),
        [
            reportData.inwardExemptNilInterState,
            reportData.inwardExemptNilIntraState,
            reportData.inwardNonGstInterState,
            reportData.inwardNonGstIntraState,
        ]
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return format(new Date(dateStr), 'MM/yyyy');
        } catch {
            return dateStr;
        }
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const headerRow = [
                'Nature of Supplies',
                'Total Taxable Value',
                'Integrated Tax',
                'Central Tax',
                'State/UT Tax',
                'Cess',
            ];
            const dataRows = buildGstr31ReportRows(
                row1,
                row2ZeroRated,
                row3NilExempt,
                row4Rcm,
                row5NonGst
            );
            const aoa = [
                ['From', formatMonthForExcel(fromMonth)],
                ['To', formatMonthForExcel(toMonth)],
                [],
                headerRow,
                [],
                ...dataRows,
            ];
            const ws = XLSX.utils.aoa_to_sheet(aoa);
            ws['!cols'] = [
                { wch: 72 },
                { wch: 18 },
                { wch: 16 },
                { wch: 14 },
                { wch: 14 },
                { wch: 10 },
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, GSTR3B_EXCEL_SHEETS.OUTWARD);
            XLSX.utils.book_append_sheet(wb, buildGstr32Worksheet(fromMonth, toMonth), GSTR3B_EXCEL_SHEETS.INTERSTATE_B2C);
            XLSX.utils.book_append_sheet(wb, buildGstr4Worksheet(fromMonth, toMonth, reportData.inwardItc), GSTR3B_EXCEL_SHEETS.ELIGIBLE_ITC);
            XLSX.utils.book_append_sheet(wb, buildGstr5Worksheet(fromMonth, toMonth, reportData), GSTR3B_EXCEL_SHEETS.EXEMPT_INWARD);
            XLSX.writeFile(wb, `GSTR3B_${startDate}_to_${endDate}.xlsx`);
            toast.success('GSTR-3B workbook exported (3.1–5)');
        } catch (error) {
            console.error('Error exporting GSTR-3B:', error);
            toast.error('Failed to export report');
        } finally {
            setIsExporting(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const onFromMonthChange = (e) => {
        const v = e.target.value;
        setFromMonth(v);
        if (v > toMonth) setToMonth(v);
    };

    const onToMonthChange = (e) => {
        const v = e.target.value;
        setToMonth(v);
        if (v < fromMonth) setFromMonth(v);
    };

    const subHeaderCellSx = {
        ...bodyCellSx,
        bgcolor: 'grey.100',
        fontWeight: 700,
        borderBottom: '1px solid',
        borderColor: 'divider',
    };

    const SectionCard = ({ title, children }) => (
        <Card sx={{ ...reportPageSx.sectionCard, mb: 1.5 }}>
            <CardContent sx={{ ...reportPageSx.sectionCardContent, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" sx={{ ...reportPageSx.sectionTitle, mb: 1 }}>
                    {title}
                </Typography>
                {children}
            </CardContent>
        </Card>
    );

    const SupplyRow = ({ row }) => (
        <TableRow sx={stripeRow}>
            <TableCell sx={{ ...bodyCellSx, maxWidth: { xs: 260, md: 400 } }}>{row.label}</TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(row.taxable)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(row.igst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(row.cgst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(row.sgst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(row.cess)}
            </TableCell>
        </TableRow>
    );

    /** ITC block: label + IGST, CGST, SGST, Cess only (no taxable column). */
    const ItcTaxRow = ({ label, igst = 0, cgst = 0, sgst = 0, cess = 0 }) => (
        <TableRow sx={stripeRow}>
            <TableCell sx={{ ...bodyCellSx, maxWidth: { xs: 260, md: 440 } }}>{label}</TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(igst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(cgst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(sgst)}
            </TableCell>
            <TableCell align="right" sx={bodyCellSx}>
                {num(cess)}
            </TableCell>
        </TableRow>
    );

    return (
        <Layout>
            <Container
                maxWidth="xl"
                sx={{
                    ...reportPageSx.container,
                    '@media print': {
                        py: 1,
                        '& .no-print': { display: 'none' },
                    },
                }}
            >
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={reportPageSx.breadcrumbs} className="no-print">
                    <Link component={RouterLink} to="/reports" color="inherit">
                        Reports
                    </Link>
                    <Typography color="text.primary">GSTR-3B</Typography>
                </Breadcrumbs>

                <Box
                    className="no-print"
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, mb: 2 }}
                >
                    <Box>
                        <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, m: 0 }}>
                            GSTR-3B Report
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Outward supplies, ITC and related schedules
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
                            onClick={handleExport}
                            disabled={isExporting || loading}
                        >
                            {isExporting ? 'Exporting…' : 'Export to Excel'}
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={handlePrint}>
                            Print
                        </Button>
                    </Box>
                </Box>

                <Card className="no-print" sx={reportPageSx.filterCard}>
                    <CardContent sx={reportPageSx.filterCardContent}>
                        <Grid
                            container
                            spacing={reportPageSx.filterGrid.spacing}
                            alignItems="flex-end"
                            sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
                        >
                            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="From Month"
                                    type="month"
                                    size="small"
                                    value={fromMonth}
                                    onChange={onFromMonthChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: toMonth }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="To Month"
                                    type="month"
                                    size="small"
                                    value={toMonth}
                                    onChange={onToMonthChange}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: fromMonth }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={fetchReportData}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                                    sx={reportPageSx.filterButton}
                                >
                                    Generate
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} className="no-print">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <SectionCard title="1. Details of outward supplies and inward supplies liable to reverse charge">
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                        <TableRow {...headRowProps}>
                                            <TableCell sx={headCellSx}>Nature of supplies</TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Total taxable value
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Integrated tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Central tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                State/UT tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Cess
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <SupplyRow row={row1} />
                                        <SupplyRow row={row2ZeroRated} />
                                        <SupplyRow row={row3NilExempt} />
                                        <SupplyRow row={row4Rcm} />
                                        <SupplyRow row={row5NonGst} />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </SectionCard>

                        <SectionCard title="2. Inter-State supplies to unregistered persons, composition dealers and UIN holders">
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small" sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow {...headRowProps}>
                                            <TableCell rowSpan={2} sx={{ ...headCellSx, verticalAlign: 'middle' }}>
                                                Place of supply (State/UT)
                                            </TableCell>
                                            <TableCell colSpan={2} align="center" sx={headCellSx}>
                                                Supplies to unregistered persons
                                            </TableCell>
                                            <TableCell colSpan={2} align="center" sx={headCellSx}>
                                                Supplies to composition taxable persons
                                            </TableCell>
                                            <TableCell colSpan={2} align="center" sx={headCellSx}>
                                                Supplies to UIN holders
                                            </TableCell>
                                        </TableRow>
                                        <TableRow {...headRowProps}>
                                            <TableCell align="center" sx={headCellSx}>
                                                Total taxable value
                                            </TableCell>
                                            <TableCell align="center" sx={headCellSx}>
                                                Integrated tax
                                            </TableCell>
                                            <TableCell align="center" sx={headCellSx}>
                                                Total taxable value
                                            </TableCell>
                                            <TableCell align="center" sx={headCellSx}>
                                                Integrated tax
                                            </TableCell>
                                            <TableCell align="center" sx={headCellSx}>
                                                Total taxable value
                                            </TableCell>
                                            <TableCell align="center" sx={headCellSx}>
                                                Integrated tax
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ ...bodyCellSx, py: 3, color: 'text.secondary' }}>
                                                No data (not tracked in system — treat as 0 for filing checks)
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </SectionCard>

                        <SectionCard title="3. Details of eligible Input Tax Credit">
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small" sx={{ minWidth: 720 }}>
                                    <TableHead>
                                        <TableRow {...headRowProps}>
                                            <TableCell sx={headCellSx}>Details</TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Integrated tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Central tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                State/UT tax
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Cess
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={subHeaderCellSx}>
                                                (A) ITC available (whether in full or part)
                                            </TableCell>
                                        </TableRow>
                                        <ItcTaxRow label="(1) Import of goods" />
                                        <ItcTaxRow label="(2) Import of services" />
                                        <ItcTaxRow label="(3) Inward supplies liable to reverse charge (other than 1 & 2 above)" />
                                        <ItcTaxRow label="(4) Inward supplies from ISD" />
                                        <ItcTaxRow
                                            label="(5) All other ITC"
                                            igst={inward.integratedTax ?? 0}
                                            cgst={inward.centralTax ?? 0}
                                            sgst={inward.stateUtTax ?? 0}
                                            cess={inward.cess ?? 0}
                                        />
                                        <TableRow>
                                            <TableCell colSpan={5} sx={subHeaderCellSx}>
                                                (D) Ineligible ITC
                                            </TableCell>
                                        </TableRow>
                                        <ItcTaxRow label="(1) As per section 17(5)" />
                                        <ItcTaxRow label="(2) Others" />
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </SectionCard>

                        <SectionCard title="4. Exempt, nil-rated and non-GST inward supplies">
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small" sx={{ minWidth: 560 }}>
                                    <TableHead>
                                        <TableRow {...headRowProps}>
                                            <TableCell sx={headCellSx}>Nature of supplies</TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Inter-State supplies
                                            </TableCell>
                                            <TableCell align="right" sx={headCellSx}>
                                                Intra-State supplies
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow sx={stripeRow}>
                                            <TableCell sx={bodyCellSx}>
                                                From a supplier under composition scheme; exempt and nil rated supply
                                            </TableCell>
                                            <TableCell align="right" sx={bodyCellSx}>
                                                {num(inwardTable5.exemptNilInter)}
                                            </TableCell>
                                            <TableCell align="right" sx={bodyCellSx}>
                                                {num(inwardTable5.exemptNilIntra)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={stripeRow}>
                                            <TableCell sx={bodyCellSx}>Non-GST supply</TableCell>
                                            <TableCell align="right" sx={bodyCellSx}>
                                                {num(inwardTable5.nonGstInter)}
                                            </TableCell>
                                            <TableCell align="right" sx={bodyCellSx}>
                                                {num(inwardTable5.nonGstIntra)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </SectionCard>

                        <Typography variant="caption" color="text.secondary" className="no-print" display="block" sx={{ mt: 1 }}>
                            Period: {formatDate(reportData.startDate || startDate)} – {formatDate(reportData.endDate || endDate)}.
                            ITC (5) follows taxable purchase lines (GSTR-2). Table 5 splits nil/exempt/non-GST purchase lines by
                            supplier GSTIN state vs company GSTIN (Settings). If the supplier has no valid GSTIN, the state saved on
                            the supplier master (State GST) is used; if both GSTIN and state are missing, values are counted as intra-state.
                            Lines use the tax master supply type; a 0% tax still stored as standard (not nil-rated) is treated as nil-rated here.
                        </Typography>
                    </Box>
                )}
            </Container>
        </Layout>
    );
};

export default GSTR3BReport;
